import {CheckIcon, XIcon, TasklistIcon} from '@primer/octicons-react'
import {MarkdownRenderer} from '@github-ui/copilot-markdown'
import type {PlanResolution} from '@github-ui/copilot-shared-ui/ahp/interactive-prompt-types'
import {stripPlanTags} from '../../utils/plan-tags'
import {ResolvedPromptDisplay} from './ResolvedPromptDisplay'
import styles from './ResolvedPlanApproval.module.css'

export interface ResolvedPlanApprovalProps {
  /** Resolution from native exit_plan_mode.completed event */
  resolution?: PlanResolution
  resolvedAnswer?: string
  isAborted?: boolean
  /** Plan summary markdown to show in the expandable content */
  summary?: string
}

/** Plan approval actions that represent an accepted/approved plan */
const APPROVAL_ACTIONS = new Set(['implement', 'autopilot', 'interactive', 'autopilot_fleet'])

/** Resolved plan approval shown in the session timeline */
export function ResolvedPlanApproval({
  resolution,
  resolvedAnswer,
  isAborted = false,
  summary,
}: ResolvedPlanApprovalProps) {
  const {icon, title, subtitle, footer} = resolveDisplayState(resolution, resolvedAnswer, isAborted)
  const cleanSummary = summary ? stripPlanTags(summary).trim() : undefined
  const expandableContent = cleanSummary ? <MarkdownRenderer markdown={cleanSummary} /> : undefined

  return (
    <ResolvedPromptDisplay icon={icon} title={title} subtitle={subtitle} defaultCollapsed footer={footer}>
      {expandableContent}
    </ResolvedPromptDisplay>
  )
}

function resolveDisplayState(
  resolution: PlanResolution | undefined,
  resolvedAnswer: string | undefined,
  isAborted: boolean,
) {
  if (isAborted) {
    return {icon: <XIcon size={16} />, title: 'Plan review', subtitle: 'Aborted by user', footer: undefined}
  }

  if (resolution) {
    if (resolution.approved && resolution.selectedAction === 'exit_only') {
      return {
        icon: <TasklistIcon size={16} />,
        title: 'Plan ready for review',
        subtitle: undefined,
        footer: <div className={styles.mutedText}>Exited plan mode</div>,
      }
    }

    if (resolution.approved) {
      return {icon: <CheckIcon size={16} />, title: 'Plan approved', subtitle: undefined, footer: undefined}
    }

    const feedbackText = resolution.feedback || 'User requested changes but did not provide specific feedback.'
    return {
      icon: <TasklistIcon size={16} />,
      title: 'Requested plan changes',
      subtitle: undefined,
      footer: <div className={styles.feedbackBubble}>{feedbackText}</div>,
    }
  }

  if (!resolvedAnswer) {
    return {icon: <CheckIcon size={16} />, title: 'Plan approved', subtitle: undefined, footer: undefined}
  }

  if (resolvedAnswer.startsWith('Feedback:')) {
    const feedbackText = resolvedAnswer.slice('Feedback:'.length).trim()
    return {
      icon: <TasklistIcon size={16} />,
      title: 'Requested plan changes',
      subtitle: undefined,
      footer: <div className={styles.feedbackBubble}>{feedbackText}</div>,
    }
  }

  if (resolvedAnswer === 'exit_only') {
    return {
      icon: <TasklistIcon size={16} />,
      title: 'Plan ready for review',
      subtitle: undefined,
      footer: <div className={styles.mutedText}>Exited plan mode</div>,
    }
  }

  if (APPROVAL_ACTIONS.has(resolvedAnswer)) {
    return {icon: <CheckIcon size={16} />, title: 'Plan approved', subtitle: undefined, footer: undefined}
  }

  // Unknown action — fall back to approved
  return {icon: <CheckIcon size={16} />, title: 'Plan approved', subtitle: undefined, footer: undefined}
}
